import type { TFunction } from 'i18next';
import type { Event, Frame } from '@/types/event';
import type {
    ReportEventDraft,
    ReportEventThumbnailFrame,
} from '@/types/report';
import { getEventPromptText } from '@/utils/detection-classes';

export interface ReportEventCandidate {
    event: Event;
    confidence?: number;
}

export function isValidBoundingBox(frame: Frame): boolean {
    if (frame.bbox?.length !== 4) {
        return false;
    }
    const [x1, y1, x2, y2] = frame.bbox;
    return (
        frame.bbox.every((coordinate) => Number.isFinite(coordinate)) &&
        x2 > x1 &&
        y2 > y1
    );
}

export function highestConfidencePerEvent(
    candidates: ReportEventCandidate[],
): ReportEventCandidate[] {
    return Array.from(
        candidates
            .reduce((byId, candidate) => {
                const current = byId.get(candidate.event.id);
                return current &&
                    (current.confidence ?? -1) >= (candidate.confidence ?? -1)
                    ? byId
                    : byId.set(candidate.event.id, candidate);
            }, new Map<string, ReportEventCandidate>())
            .values(),
    );
}

export function defaultThumbnailFrames(
    event: Event,
): ReportEventThumbnailFrame[] {
    const frame =
        event.type === 'SCENE'
            ? (event.frames.find(
                  (candidate) =>
                      candidate.id === event.snapshotFrameId &&
                      isValidBoundingBox(candidate),
              ) ?? event.frames.find(isValidBoundingBox))
            : event.frames.find(isValidBoundingBox);
    return frame ? [{ time: frame.time, bbox: frame.bbox }] : [];
}

interface InitialDraftsArgs {
    candidates: ReportEventCandidate[];
    t: TFunction;
    query?: string | null;
}

export function initialReportEventDrafts({
    candidates,
    t,
    query,
}: InitialDraftsArgs): ReportEventDraft[] {
    return highestConfidencePerEvent(candidates).map(
        ({ event, confidence }) => ({
            event,
            confidence,
            promptText: getEventPromptText(t, event, query),
            description: '',
            thumbnails: defaultThumbnailFrames(event),
        }),
    );
}
